import { HttpResourceRef } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  IApiErrorResponse,
  IApiPaginatedStandardResponse
} from '@core/models/api/api.models';
import { IRequestPaginationData } from '@core/services/api/api.service';
import { IMail, IUser } from '@core/models/user.model';
import { ApiService } from '@core/services/api/api.service';
import { Console } from './logger.service';

/**
 * Interfaccia per la richiesta di creazione di un nuovo utente amministratore.
 */
export interface IUserAdminCreateRequest {
  nome: string;
  cognome: string;
  email: string;
  password: string;
  password_confirmation: string;
  codice_fiscale: string;
  cellulare: string;
  roles: string;
  target?: string;
  id_anagrafica?: number;
  id_sponsor?: number;
  id_segnalatore?: number;
  privacy_trattamento_dati: boolean;
  privacy_dati_sensibili: boolean;
  privacy_attivita_commerciali: boolean;
  privacy_attivita_conto_terzi: boolean;
}

/**
 * Interfaccia per la richiesta di aggiornamento di un utente amministratore.
 * Tutti i campi sono opzionali per permettere aggiornamenti parziali.
 */
export interface IUserAdminUpdateRequest {
  nome?: string;
  cognome?: string;
  email?: string;
  cellulare?: string;
  codice_fiscale?: string;
  roles?: string;
  target?: string;
  id_anagrafica?: number;
  privacy_trattamento_dati?: boolean;
  privacy_dati_sensibili?: boolean;
  privacy_attivita_commerciali?: boolean;
  privacy_attivita_conto_terzi?: boolean;
}

/**
 * Interfaccia per la richiesta di aggiornamento password di un utente amministratore.
 */
export interface IUserAdminPasswordUpdateRequest {
  password: string;
  password_confirmation: string;
}

/**
 * Interfaccia per la richiesta di blocco/sblocco di un utente amministratore.
 */
export interface IUserAdminBlockRequest {
  bloccato: boolean;
}

/**
 * Servizio per la gestione amministrativa degli utenti.
 *
 * Questo servizio fornisce metodi per tutte le operazioni CRUD sugli utenti
 * che richiedono privilegi amministrativi:
 * - Elenco utenti paginato
 * - Dettaglio singolo utente
 * - Creazione nuovo utente
 * - Aggiornamento dati utente
 * - Aggiornamento password utente
 * - Blocco/sblocco utente
 *
 * Tutte le chiamate utilizzano ApiService per la gestione HTTP e degli errori.
 *
 * **Standard Response API:**
 * - Risposte positive (HTTP 200): seguono lo standard `{ message: string, data?: T }`
 *   - Il campo `data` viene estratto automaticamente da ApiService
 * - Risposte di errore: seguono lo standard `IApiErrorResponse`
 *   - HTTP 422 (Unprocessable Content): errori di validazione con campo `errors` (oggetto con chiavi campo e valori array di messaggi)
 *   - HTTP 401 (Unauthorized): errore di autenticazione con campo `error`
 *   - HTTP 403 (Forbidden): errore di autorizzazione con campo `error`
 *   - Tutti gli errori hanno sempre il campo `status_code` e `message`
 *
 * Gli errori vengono normalizzati da ApiService e possono essere gestiti nei componenti
 * verificando `status_code` e i campi `errors` (per 422) o `error` (per 401/403).
 *
 * @example
 * ```typescript
 * constructor(private userAdminService: UserAdminService) {}
 *
 * // Elenco utenti paginato
 * const pagination: IRequestPaginationData = {
 *   page: 1,
 *   per_page: 15,
 *   sort_by: 'created_at',
 *   sort_order: 'desc',
 *   search: ''
 * };
 * this.userAdminService.getUsers(pagination).subscribe({
 *   next: (response) => console.log('Utenti:', response.data),
 *   error: (error) => console.error('Errore:', error)
 * });
 *
 * // Creazione nuovo utente
 * const newUser: IUserAdminCreateRequest = {
 *   nome: 'MARIO',
 *   cognome: 'ROSSI',
 *   email: 'mario.rossi@example.com',
 *   password: 'Password123!',
 *   password_confirmation: 'Password123!',
 *   codice_fiscale: 'RSSMRA85M01H501Z',
 *   cellulare: '+393491234567',
 *   roles: 'user',
 *   privacy_trattamento_dati: true,
 *   privacy_dati_sensibili: true,
 *   privacy_attivita_commerciali: true,
 *   privacy_attivita_conto_terzi: true
 * };
 * this.userAdminService.createUser(newUser).subscribe({
 *   next: (user) => console.log('Utente creato:', user),
 *   error: (error) => console.error('Errore creazione:', error)
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class UserAdminService {
  private readonly log_label = 'UserAdminService';
  private readonly api = inject(ApiService);

  private static readonly EMPTY_USERS_PAGE: IApiPaginatedStandardResponse<IUser> = {
    message: '',
    data: [],
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 15,
      total: 0,
      from: 0,
      to: 0,
      sort_by: 'created_at',
      sort_order: 'desc',
      search: '',
      sortable_fields: []
    }
  };

  /**
   * Resource reattiva per l'elenco utenti paginato (Angular 22 `httpResource`).
   */
  createUsersResource(
    shouldLoad: () => boolean,
    pagination: () => IRequestPaginationData | undefined,
    extraParams?: () => Record<string, string | number | boolean> | undefined
  ): HttpResourceRef<IApiPaginatedStandardResponse<IUser>> {
    return this.api.getPaginatedResource<IUser>(
      () => (shouldLoad() ? 'user/admin/users' : undefined),
      pagination,
      extraParams,
      {
        defaultValue: UserAdminService.EMPTY_USERS_PAGE,
        debugName: 'adminUsers'
      }
    );
  }

  /**
   * Recupera l'elenco degli utenti con paginazione.
   *
   * @param pagination - Dati di paginazione (page, per_page, sort_by, sort_order, search)
   * @param extraParams - Parametri aggiuntivi opzionali da passare come query string
   * @returns Observable contenente la risposta paginata con array di utenti e dati di paginazione
   */
  getUsers(
    pagination: IRequestPaginationData,
    extraParams?: Record<string, string | number | boolean>
  ): Observable<IApiPaginatedStandardResponse<IUser>> {
    const log_pref = this.log_label + '.getUsers() ';
    return this.api.getPaginated<IUser>('user/admin/users', pagination, extraParams).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nel recupero dell\'elenco utenti.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera i dettagli di un singolo utente tramite ID.
   *
   * @param id - ID dell'utente da recuperare
   * @returns Observable contenente i dati completi dell'utente
   */
  getUserById(id: number): Observable<IUser> {
    const log_pref = this.log_label + '.getUserById() ';
    return this.api.get<IUser>(`user/admin/users/${id}`).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nel recupero dell'utente con ID ${id}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un nuovo utente.
   *
   * @param payload - Dati completi del nuovo utente da creare
   * @returns Observable contenente i dati dell'utente creato
   */
  createUser(payload: IUserAdminCreateRequest): Observable<IUser> {
    const log_pref = this.log_label + '.createUser() ';
    return this.api.post<IUser, IUserAdminCreateRequest>('user/admin/users', payload).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nella creazione del nuovo utente.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna i dati di un utente esistente.
   *
   * @param id - ID dell'utente da aggiornare
   * @param payload - Dati parziali da aggiornare (tutti i campi sono opzionali)
   * @returns Observable contenente i dati aggiornati dell'utente
   */
  updateUser(id: number, payload: IUserAdminUpdateRequest): Observable<IUser> {
    const log_pref = this.log_label + '.updateUser() ';
    return this.api.patch<IUser, IUserAdminUpdateRequest>(
      `user/admin/users/${id}`,
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nell'aggiornamento dell'utente con ID ${id}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna la password di un utente.
   *
   * @param id - ID dell'utente di cui aggiornare la password
   * @param payload - Nuova password e conferma password
   * @returns Observable contenente la risposta (tipicamente un messaggio di successo)
   */
  updateUserPassword(
    id: number,
    payload: IUserAdminPasswordUpdateRequest
  ): Observable<string> {
    const log_pref = this.log_label + '.updateUserPassword() ';
    return this.api.patch<string, IUserAdminPasswordUpdateRequest>(
      `user/admin/users/${id}/password`,
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nell'aggiornamento della password dell'utente con ID ${id}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Forza il cambio password per un utente impostando lo stato "password scaduta".
   *
   * @param id - ID dell'utente per cui forzare il cambio password
   * @returns Observable contenente i dati aggiornati dell'utente
   */
  forceUserPasswordChange(id: number): Observable<IUser> {
    const log_pref = this.log_label + '.forceUserPasswordChange() ';
    return this.api.patch<IUser, { id: number }>(
      `user/admin/users/${id}/password-expired`,
      { id }
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nel forzare il cambio password per l'utente con ID ${id}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Blocca o sblocca un utente.
   *
   * @param id - ID dell'utente da bloccare/sbloccare
   * @param blocked - true per bloccare l'utente, false per sbloccarlo
   * @returns Observable contenente i dati aggiornati dell'utente o un messaggio di successo
   */
  setUserBlocked(id: number, blocked: boolean): Observable<IUser | string> {
    const log_pref = this.log_label + '.setUserBlocked() ';
    const payload: IUserAdminBlockRequest = { bloccato: blocked };
    return this.api.patch<IUser | string, IUserAdminBlockRequest>(
      `user/admin/users/${id}/block`,
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const action = blocked ? 'blocco' : 'sblocco';
        const errorMessage = log_pref + (error?.message || `Errore nel ${action} dell'utente con ID ${id}.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Ricerca caselle di posta webmail il cui `user_name` contiene la stringa indicata.
   *
   * @param ricerca - Testo da cercare (es. parte dell'indirizzo email)
   * @returns Observable con l'array di caselle (`IApiMail.data`)
   */
  searchMailboxes(ricerca: string): Observable<IMail[]> {
    const log_pref = this.log_label + '.searchMailboxes() ';
    const encoded = encodeURIComponent(ricerca.trim());
    return this.api.get<IMail[]>(`user/admin/mailbox/${encoded}`).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage =
          log_pref + (error?.message || 'Errore nella ricerca delle caselle di posta.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }
}
